import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, 
  Blocks, 
  Cpu, 
  Database, 
  Bot, 
  Grid, 
  MessageSquare, 
  Network, 
  Search, 
  Workflow,
  Zap 
} from 'lucide-react';

const technologies = [
  {
    name: 'n8n',
    icon: Network,
    description: 'The "digital plumber" that connects all your business apps so they work together like a well-oiled machine.',
    category: 'Automation Platform',
    features: ['Connects 400+ apps', 'No coding needed', 'Works 24/7'],
    gradient: 'from-orange-500/20 to-red-500/20',
    explanation: 'Imagine never having to copy information between apps again. n8n makes it happen automatically.',
    painPoint: 'Tired of copying data between different apps all day?'
  },
  {
    name: 'Make',
    icon: Zap,
    description: 'Your super-organized digital assistant that handles complex tasks across multiple apps simultaneously.',
    category: 'Integration Platform',
    features: ['2000+ app connections', 'Real-time automation', 'Smart decision making'],
    gradient: 'from-blue-500/20 to-indigo-500/20',
    explanation: 'Like having a perfect employee who can juggle 20 tasks at once and never makes mistakes.',
    painPoint: 'Overwhelmed by managing multiple systems and processes?'
  },
  {
    name: 'WhatsApp Business API',
    icon: MessageSquare,
    description: 'The official way to build chatbots that talk to your customers where they already spend their time.',
    category: 'Messaging Platform',
    features: ['Official WhatsApp integration', 'Rich media support', 'Business features'],
    gradient: 'from-green-500/20 to-emerald-600/20',
    explanation: 'Your customers are already on WhatsApp. Now your business can be there too, 24/7.',
    painPoint: 'Missing customers because you can\'t respond to messages fast enough?'
  },
  {
    name: 'OpenAI GPT',
    icon: Brain,
    description: 'The AI brain that makes our chatbots and voice agents sound human and understand what people really want.',
    category: 'AI Language Model',
    features: ['Natural conversation', 'Context understanding', 'Multi-language support'],
    gradient: 'from-purple-500/20 to-pink-500/20',
    explanation: 'This is what makes customers think they\'re talking to a real person, not a robot.',
    painPoint: 'Want AI that actually understands your customers?'
  },
  {
    name: 'Voiceflow',
    icon: MessageSquare,
    description: 'The platform we use to design conversations that feel natural and handle any situation your customers throw at them.',
    category: 'Conversation Design',
    features: ['Visual conversation builder', 'Voice & chat support', 'Analytics dashboard'],
    gradient: 'from-blue-500/20 to-violet-500/20',
    explanation: 'We map out every possible conversation so your AI always knows what to say.',
    painPoint: 'Worried about AI saying the wrong thing to customers?'
  },
  {
    name: 'Twilio',
    icon: Bot,
    description: 'The communication platform that lets our AI agents make and receive real phone calls for your business.',
    category: 'Voice Platform',
    features: ['Phone call handling', 'Voice recognition', 'Global coverage'],
    gradient: 'from-red-500/20 to-pink-500/20',
    explanation: 'Your AI receptionist can actually pick up the phone and have real conversations.',
    painPoint: 'Tired of being interrupted by phone calls all day?'
  }
];

function TechnologyCard({ tech }: { tech: typeof technologies[0] }) {
  return (
    <div className="group relative flex">
      <div className="relative bg-white rounded-xl transition-all duration-500 transform-gpu hover:shadow-2xl hover:shadow-teal-500/10 border border-gray-100 flex flex-col w-full">
        <div className="magnetic-glow" />
        
        <div className="relative z-10 flex flex-col h-full p-8">
          <div className="h-16 mb-6">
            <div className={`
              w-16 h-16 rounded-xl
              bg-gradient-to-br ${tech.gradient}
              flex items-center justify-center
              group-hover:scale-110 transition-transform duration-500
            `}>
              <tech.icon className="h-8 w-8 text-gray-900" />
            </div>
          </div>

          <div className="mb-5">
            <div className="inline-block bg-red-50 text-red-600 px-3 py-1.5 rounded-md border border-red-200 mb-3 shadow-sm">
              <p className="text-sm font-medium">{tech.painPoint}</p>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-teal-600 leading-tight">
              {tech.name}
            </h3>
            <p className="text-sm text-teal-600 font-medium">{tech.category}</p>
          </div>
          
          <div className="mb-8">
            <p className="text-gray-600 text-lg leading-snug mb-4">
              {tech.description}
            </p>
            <p className="text-sm text-gray-500 italic">
              {tech.explanation}
            </p>
          </div>

          <div className="mt-auto space-y-3">
            {tech.features.map((feature, i) => (
              <div key={i} className="flex items-center text-sm text-teal-600">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mr-2" />
                {feature}
              </div>
            ))}
          </div>
        </div>

        <div className="card-shadow" />
      </div>
    </div>
  );
}

export default function Technologies() {
  return (
    <section className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold mb-6">
            <span className="text-gradient">The Tools That Set You Free</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-4">
            We use the most powerful automation tools available to build AI helpers that actually work. 
            Here's what powers your freedom from daily business chaos:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {technologies.map((tech) => (
            <TechnologyCard key={tech.name} tech={tech} />
          ))}
        </div>

        <div className="bg-gray-50 rounded-2xl p-10 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">How These Tools Give You Your Life Back</h3>
          <div className="grid md:grid-cols-3 gap-10 mt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-teal-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">1. Customer Contacts You</h4>
              <p className="text-gray-600">Your customer messages your WhatsApp bot or calls your voice agent - even at 2am</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Brain className="h-8 w-8 text-purple-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">2. AI Understands & Helps</h4>
              <p className="text-gray-600">Our AI understands what they need and provides instant, helpful responses</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">3. You Stay Free</h4>
              <p className="text-gray-600">Everything happens automatically while you enjoy dinner with your family</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="max-w-3xl mx-auto mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Stop Working Nights and Weekends?</h3>
            <p className="text-lg text-gray-600 mt-4">
              Take our quick assessment to see how ready your business is for automation that actually works, 
              or let's have a conversation about getting your life back.
            </p>
          </div>
          
          <Link 
            to="/tools/ai-readiness-assessment"
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-md text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
          >
            <Brain className="h-5 w-5 mr-2" />
            <span>See If You're Ready for Freedom</span>
          </Link>
        </div>
      </div>
    </section>
  );
}